import type { Chart, Plugin } from "chart.js";

import { computedColorOf } from "@/shared/charts";

/**
 * How strongly the band tints its slot: the foreground colour at this opacity.
 * Translucent rather than a solid grey so the gridlines it is drawn over still
 * show through, and toward the foreground so it reads the same in light and
 * dark themes (the hovered bars mix toward the same colour).
 */
const HOVER_BAND_ALPHA = 0.08;

/**
 * Shades the whole slot of the hovered bucket, from one bucket boundary to the
 * next and from the top of the plot to the bottom, behind the series.
 *
 * Hovering already activates every series in the bucket (`interaction.mode`
 * is `index`) and a click drills into the whole bucket. The band makes that
 * target visible, including between and above the points, where nothing else
 * says which bucket the pointer is in.
 *
 * For the BUCKETED axis only, which places bucket `i` at x = `i` (see
 * `datasetData`), so its slot is `i ± 0.5`. The time-proportional axis has no
 * slots: each point is one session at its own instant.
 *
 * Drawn before the datasets so lines, points and bars stay on top. Skipped
 * while the tooltip is switched off for a pan or zoom (`setTooltipEnabled`):
 * like the tooltip, the band is a reading of where the pointer rests.
 */
export const hoverBandPlugin: Plugin = {
  id: "hoverBand",
  beforeDatasetsDraw(chart: Chart) {
    if (chart.options.plugins?.tooltip?.enabled === false) {
      return;
    }
    const index = chart.getActiveElements()[0]?.index;
    const x = chart.scales.x;
    if (index === undefined || x === undefined) {
      return;
    }
    const area = chart.chartArea;
    // Clipped to the plot: a zoomed or panned axis can put an edge bucket's
    // slot partly outside it.
    const left = Math.max(x.getPixelForValue(index - 0.5), area.left);
    const right = Math.min(x.getPixelForValue(index + 0.5), area.right);
    if (right <= left) {
      return;
    }
    const { ctx } = chart;
    ctx.save();
    ctx.globalAlpha = HOVER_BAND_ALPHA;
    ctx.fillStyle = computedColorOf(chart.canvas, "--gray-12");
    ctx.fillRect(left, area.top, right - left, area.bottom - area.top);
    ctx.restore();
  },
};
